import { Check, Package, Search, ShoppingCart, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Avatar, discordAvatarUrl } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { Dialog } from '../../../components/Dialog';
import { Input } from '../../../components/Input';
import { Segmented, type SegmentedOption } from '../../../components/Segmented';
import { cn } from '../../../lib/cn';
import { useListProductsQuery } from '../../products/products.api';
import { useListUsersQuery } from '../../users/users.api';
import { ticketErrorRO } from '../ticketCopy';
import { useAddReferenceMutation, useSearchOrdersQuery } from '../tickets.api';
import { TICKET_REFERENCE_LABELS, type TicketReferenceType } from '../tickets.schema';

interface Props {
    /** Ticket id — omit in create mode (pair with onPick). */
    id?: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Create mode: hand the picked reference to the caller instead of POSTing
        (the ticket doesn't exist yet, so there's nothing to attach it to). */
    onPick?: (ref: { itemType: TicketReferenceType; itemId: string; label: string }) => void;
}

const TYPE_OPTIONS: SegmentedOption<TicketReferenceType>[] = [
    { value: 'order', label: TICKET_REFERENCE_LABELS.order, icon: ShoppingCart },
    { value: 'user', label: TICKET_REFERENCE_LABELS.user, icon: User },
    { value: 'product', label: TICKET_REFERENCE_LABELS.product, icon: Package },
];

const TYPE_ICONS = { order: ShoppingCart, user: User, product: Package } as const;

const SEARCH_PLACEHOLDERS: Record<TicketReferenceType, string> = {
    order: 'Cauta comanda (nr, nume, email)...',
    user: 'Cauta utilizator dupa nume...',
    product: 'Cauta produs (titlu, sku, barcode)...',
};

type Result = { itemId: string; label: string; sub: string | null; src: string | null; seed: number };

export function AddReferenceDialog({ id, open, onOpenChange, onPick }: Props) {
    const [addReference, { isLoading }] = useAddReferenceMutation();
    const [itemType, setItemType] = useState<TicketReferenceType>('order');
    const [searchInput, setSearchInput] = useState('');
    const [query, setQuery] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            setItemType('order');
            setSearchInput('');
            setQuery('');
            setSelectedId(null);
        }
    }, [open]);

    useEffect(() => {
        const t = window.setTimeout(() => setQuery(searchInput.trim()), 300);
        return () => window.clearTimeout(t);
    }, [searchInput]);

    const usersQ = useListUsersQuery(
        { search: query || undefined, active: true, limit: 25 },
        { skip: !open || itemType !== 'user' },
    );
    const ordersQ = useSearchOrdersQuery(query || undefined, { skip: !open || itemType !== 'order' });
    const productsQ = useListProductsQuery(
        { search: query || undefined, limit: 25 },
        { skip: !open || itemType !== 'product' },
    );
    const loading =
        itemType === 'user' ? usersQ.isFetching : itemType === 'product' ? productsQ.isFetching : ordersQ.isFetching;

    const results: Result[] = useMemo(() => {
        if (itemType === 'user') {
            return (usersQ.data?.data ?? []).map((u) => ({
                itemId: String(u.id),
                label: u.name || u.email,
                sub: u.name ? u.email : null,
                src: discordAvatarUrl(u.discordId, u.discordAvatar),
                seed: u.id,
            }));
        }
        if (itemType === 'product') {
            return (productsQ.data?.data ?? []).map((p) => ({
                itemId: String(p.id),
                label: p.title,
                sub: p.barcode || p.sku || null,
                src: null,
                seed: p.id,
            }));
        }
        return (ordersQ.data ?? []).map((o) => ({
            itemId: String(o.id),
            label: `#${o.orderNumber || o.id}`,
            sub: o.billingName || o.deliveryName || o.email || null,
            src: null,
            seed: o.id,
        }));
    }, [itemType, usersQ.data, ordersQ.data, productsQ.data]);

    const selected = results.find((r) => r.itemId === selectedId) ?? null;
    // Icon for the non-user result rows / selected box (users render an Avatar).
    const TypeIcon = TYPE_ICONS[itemType];

    const submit = async () => {
        if (!selected) {
            toast.error(
                itemType === 'user' ? 'Alege un utilizator.' : itemType === 'product' ? 'Alege un produs.' : 'Alege o comanda.',
            );
            return;
        }
        if (onPick) {
            onPick({ itemType, itemId: selected.itemId, label: selected.label });
            onOpenChange(false);
            return;
        }
        if (id == null) return;
        try {
            await addReference({ id, itemType, itemId: selected.itemId, label: selected.label }).unwrap();
            toast.success('Legatura a fost adaugata.');
            onOpenChange(false);
        } catch (e) {
            toast.error(ticketErrorRO(e, 'Eroare la adaugarea legaturii.'));
        }
    };

    return (
        <Dialog
            open={open}
            closeOnBackdrop={false}
            onOpenChange={onOpenChange}
            maxWidth="max-w-3xl"
            fixedHeight="h-[min(85vh,42rem)]"
            title="Adauga legatura"
            description="Leaga ticketul de o comanda sau un utilizator."
            footer={
                <>
                    <Button type="button" variant="secondary" size="lg" onClick={() => onOpenChange(false)} disabled={isLoading}>
                        Anuleaza
                    </Button>
                    <Button type="button" size="lg" onClick={submit} disabled={isLoading || !selected}>
                        {isLoading ? 'Se adauga...' : 'Adauga'}
                    </Button>
                </>
            }
        >
            {/* Two columns on desktop: controls on the left, a tall results list on
                the right (its own scroll). Stacks to a single column on phones. */}
            <div className="flex h-full flex-col gap-4 md:flex-row md:gap-6">
                <div className="flex shrink-0 flex-col gap-3 md:w-72">
                    <Segmented
                        block
                        value={itemType}
                        onChange={(t) => {
                            setItemType(t);
                            setSelectedId(null);
                        }}
                        options={TYPE_OPTIONS}
                        ariaLabel="Tipul legaturii"
                    />
                    <Input
                        type="search"
                        icon={Search}
                        className="h-12 text-base"
                        placeholder={SEARCH_PLACEHOLDERS[itemType]}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        aria-label="Cauta"
                    />
                    {selected ? (
                        <div className="flex items-center gap-3 rounded-xl border border-solid border-blue-200 bg-blue-50 px-3 py-2.5 dark:border-blue-900 dark:bg-blue-900/20">
                            {itemType === 'user' ? (
                                <Avatar src={selected.src} name={selected.label} seed={selected.seed} className="h-9 w-9 text-xs" />
                            ) : (
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300">
                                    <TypeIcon aria-hidden="true" className="h-5 w-5" />
                                </span>
                            )}
                            <span className="min-w-0 flex-1">
                                <span className="block text-xs font-medium uppercase tracking-wide text-blue-600 dark:text-blue-300">
                                    Selectat
                                </span>
                                <span className="block truncate font-semibold text-gray-900 dark:text-gray-100">
                                    {selected.label}
                                </span>
                            </span>
                        </div>
                    ) : (
                        <p className="hidden text-sm text-gray-500 dark:text-gray-400 md:block">
                            Cauta, apoi apasa pe un rezultat din dreapta.
                        </p>
                    )}
                </div>

                <ul className="v2-scroll mb-2 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-2xl bg-gray-50 p-2 dark:bg-gray-900/40">
                    {loading ? (
                        <li className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">Se cauta...</li>
                    ) : results.length === 0 ? (
                        <li className="px-3 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                            {query ? 'Niciun rezultat.' : 'Scrie mai sus pentru a cauta.'}
                        </li>
                    ) : (
                        results.map((r) => {
                            const isSel = selectedId === r.itemId;
                            return (
                                <li key={r.itemId}>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedId(r.itemId)}
                                        className={cn(
                                            'flex w-full items-center gap-3 rounded-xl border border-solid px-3 py-2.5 text-left transition-all',
                                            isSel
                                                ? 'border-blue-500 bg-blue-50 ring-1 ring-inset ring-blue-500 dark:border-blue-500 dark:bg-blue-950/40'
                                                : 'border-gray-200 bg-white shadow-sm hover:border-blue-300 hover:shadow dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-700',
                                        )}
                                    >
                                        {itemType === 'user' ? (
                                            <Avatar src={r.src} name={r.label} seed={r.seed} className="h-10 w-10 text-sm" />
                                        ) : (
                                            <span
                                                className={cn(
                                                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                                                    isSel
                                                        ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300'
                                                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300',
                                                )}
                                            >
                                                <TypeIcon aria-hidden="true" className="h-5 w-5" />
                                            </span>
                                        )}
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate font-semibold text-gray-900 dark:text-gray-100">
                                                {r.label}
                                            </span>
                                            {r.sub && (
                                                <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                                    {r.sub}
                                                </span>
                                            )}
                                        </span>
                                        {isSel && (
                                            <Check aria-hidden="true" className="ml-1 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
                                        )}
                                    </button>
                                </li>
                            );
                        })
                    )}
                </ul>
            </div>
        </Dialog>
    );
}
